import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, Shield, Clock, Star, MapPin } from 'lucide-react';
import { CitySearch } from '../components/CitySearch';
import { PlumberCard } from '../components/PlumberCard';
import { LocationMap } from '../components/LocationMap';
import { SchemaMarkup } from '../components/SchemaMarkup';
import { supabase } from '../lib/supabase';
import { FAQSection } from '../components/FAQSection';
import { usePlumberSearch } from '../hooks/usePlumberSearch';
import { ServiceCard } from '../components/ServiceCard';
import toast from 'react-hot-toast';

interface City {
  id: string;
  name: string;
  slug: string;
  zip_codes: string[];
  description: string;
  county: string;
  latitude: number;
  longitude: number;
  state: {
    code: string;
    name: string;
    about_template: string;
    why_choose_us_template: string;
    benefits_template: string;
  };
}

interface Service {
  id: string;
  title: string;
  description: string;
}

function CityPage() {
  const { citySlug, stateCode } = useParams<{ citySlug: string; stateCode: string }>();
  const [city, setCity] = useState<City | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { plumbers, loading: plumbersLoading } = usePlumberSearch(city?.zip_codes?.[0] || '', {});
  const navigate = useNavigate();

  // Create markers for each ZIP code with slightly offset positions
  const mapMarkers = React.useMemo(() => {
    if (!city) return [];
    if (!city.zip_codes) return [];
    
    // Get city coordinates from Supabase
    return city.zip_codes.map(zip => {
      return {
        position: [city.latitude, city.longitude] as [number, number],
        popup: (
          <div className="text-center">
            <div className="font-semibold mb-2">
              {city.name}, {city.state.code}
            </div>
            <div className="text-sm text-gray-600 mb-2">
              ZIP Code: {zip}
            </div>
            <button
              onClick={() => navigate(`/search/${zip}`)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              Find Plumbers
            </button>
          </div>
        ),
        zipCode: zip
      };
    });
  }, [city]);

  // Calculate map bounds to fit all markers
  const mapBounds = React.useMemo(() => {
    if (!mapMarkers.length) return undefined;

    const latitudes = mapMarkers.map(m => m.position[0]);
    const longitudes = mapMarkers.map(m => m.position[1]);
    
    return [
      [Math.min(...latitudes) - 0.1, Math.min(...longitudes) - 0.1],
      [Math.max(...latitudes) + 0.1, Math.max(...longitudes) + 0.1]
    ] as [[number, number], [number, number]];
  }, [mapMarkers]);

  useEffect(() => {
    async function loadCity() {
      try {
        setLoading(true);
        setError(null);
        
        if (!citySlug || !stateCode) {
          throw new Error('City slug and state code are required');
        }
        
        // Normalize the slug by converting spaces to hyphens and removing special characters
        const normalizedSlug = citySlug.toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        
        // Get city data with state info
        // First get the state ID
        const { data: stateData, error: stateError } = await supabase
          .from('states')
          .select('id, code')
          .eq('code', stateCode.toUpperCase())
          .single();

        if (stateError) throw stateError;
        if (!stateData) throw new Error('State not found');

        // Get the city with state info
        const { data: cityData, error: cityError } = await supabase
          .from('cities')
          .select(`
            id,
            name,
            slug,
            zip_codes,
            description,
            county,
            latitude,
            longitude,
            state:state_id (
              code,
              name,
              about_template,
              why_choose_us_template,
              benefits_template
            )
          `)
          .eq('slug', normalizedSlug)
          .eq('state_id', stateData.id)
          .single();

        if (cityError) throw cityError;
        if (!cityData) {
          throw new Error(`No plumbing services found in ${normalizedSlug.replace(/-/g, ' ')}, ${stateCode.toUpperCase()}`);
        }
        
        // Transform the data to match our City interface
        const city = {
          id: cityData.id,
          name: cityData.name,
          slug: cityData.slug,
          zip_codes: cityData.zip_codes || [],
          description: cityData.description || '',
          county: cityData.county || '',
          latitude: cityData.latitude || 0,
          longitude: cityData.longitude || 0,
          state: {
            code: cityData.state.code,
            name: cityData.state.name,
            about_template: cityData.state.about_template || '',
            why_choose_us_template: cityData.state.why_choose_us_template || '',
            benefits_template: cityData.state.benefits_template || ''
          }
        };
        
        setCity(city);

        // Get both city-specific and global FAQs
        const [{ data: cityFaqs, error: cityFaqsError }, { data: globalFaqs, error: globalFaqsError }] = await Promise.all([
          supabase
            .from('faqs')
            .select('*')
            .eq('city_id', city.id)
            .order('order'),
          supabase
            .from('faqs')
            .select('*')
            .is('city_id', null)
            .is('service_id', null)
            .order('order')
        ]);

        if (cityFaqsError) throw cityFaqsError;
        if (globalFaqsError) throw globalFaqsError;

        // Process FAQs to replace placeholders
        const processedFaqs = [...(cityFaqs || []), ...(globalFaqs || [])].map(faq => ({
          ...faq,
          question: faq.question
            .replace(/\{city\}/g, city.name)
            .replace(/\{state\}/g, city.state.name)
            .replace(/\{state_code\}/g, city.state.code),
          answer: faq.answer
            .replace(/\{city\}/g, city.name)
            .replace(/\{state\}/g, city.state.name)
            .replace(/\{state_code\}/g, city.state.code)
        }));

        setFaqs(processedFaqs);
        setError(null);

        // Load services after city is loaded
        try {
          const { data: servicesData, error: servicesError } = await supabase
            .from('services')
            .select('id, title, description')
            .order('title');

          if (servicesError) throw servicesError;
          setServices(servicesData || []);
        } catch (servicesError) {
          console.error('Error loading services:', servicesError);
          // Don't show toast for services error as it's not critical
        }
      } catch (error) {
        console.error('Error loading city:', error);
        const message = error instanceof Error ? error.message : 'Failed to load city';
        setError(message);
        toast.error(message);
        setCity(null);
        setFaqs([]);
      } finally {
        setLoading(false);
      }
    }

    loadCity();
  }, [citySlug, stateCode]);

  // Generate spun content variations
  const generateSpunContent = (template: string, city: string) => {
    const variations = {
      'Professional': ['Expert', 'Skilled', 'Experienced', 'Qualified', 'Trusted'],
      'plumbing services': ['plumbing solutions', 'plumbing assistance', 'plumbing help', 'plumbing care'],
      'available': ['ready', 'on call', 'at your service', 'accessible'],
      'licensed': ['certified', 'qualified', 'accredited', 'authorized'],
      'insured': ['bonded', 'protected', 'covered', 'guaranteed'],
      'plumbers': ['plumbing professionals', 'plumbing experts', 'plumbing specialists', 'plumbing technicians']
    };

    let spunContent = template.replace(/\{city\}/g, city);
    
    Object.entries(variations).forEach(([word, alternatives]) => {
      const randomVariation = alternatives[Math.floor(Math.random() * alternatives.length)];
      spunContent = spunContent.replace(new RegExp(word, 'gi'), randomVariation);
    });

    return spunContent;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">
          <div className="animate-pulse flex items-center">
            <MapPin className="w-6 h-6 mr-2" />
            Loading city information...
          </div>
        </div>
      </div>
    );
  }

  if (!city) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col space-y-6">
        <div className="text-xl text-gray-600 text-center max-w-lg mx-auto px-4 mb-4">
          {error || 'City not found'}
        </div>
        <Link 
          to="/locations" 
          className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <MapPin className="w-5 h-5 mr-2" />
          View All Locations
        </Link>
      </div>
    );
  }

  // Generate spun content for each section
  const aboutContent = city.state.about_template?.replace(/\{city\}/g, city.name) || '';
  const whyChooseUsContent = city.state.why_choose_us_template?.replace(/\{city\}/g, city.name) || '';
  const benefitsContent = city.state.benefits_template?.replace(/\{city\}/g, city.name) || '';

  return (
    <>
      <Helmet>
        <title>{`Best ${city.name} Plumbers | 24/7 Emergency Service - Plumbers.Guru`}</title>
        <meta name="description" content={generateSpunContent(`Professional plumbing services in {city}. 24/7 emergency service, licensed plumbers, and fair pricing. Expert solutions for all your plumbing needs.`, city.name)} />
      </Helmet>

      <div className="bg-white">
        <div className="relative bg-blue-600 py-24">
          <div className="absolute inset-0">
            <img
              className="w-full h-full object-cover opacity-20"
              src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&q=80"
              alt="Plumbing background"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white mb-4">
                Best Plumbers in {city.name}, {city.state?.code}
              </h1>
              <p className="text-xl text-gray-100 max-w-3xl mx-auto">
                {city.description}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Local Plumbers</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map(service => (
              <ServiceCard key={service.id} service={service} />
            ))}
          </div>

          {/* Service Area Map */}
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Service Area</h2>
            <LocationMap
              center={[city.latitude, city.longitude]}
              bounds={mapBounds}
              markers={mapMarkers.map(marker => ({
                ...marker,
                popup: marker.popup
              }))}
              onMarkerClick={(zipCode) => navigate(`/search/${zipCode}`)}
            />
            <div className="mt-4 text-center text-gray-600">
              <p>
                Serving {city.name}, {city.state.code} and surrounding areas:
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {city.zip_codes.map(zip => (
                  <Link
                    key={zip}
                    to={`/${city.state.code.toLowerCase()}/${city.slug}/${zip}`}
                    className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 rounded-full hover:bg-blue-200 transition-colors"
                  >
                    {zip}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Why Choose Us Section */}
          <div className="mt-24">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Why Choose Us in {city.name}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">Local Expertise</h3>
                <p className="text-gray-600">{aboutContent}</p>
              </div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">Why Choose Us</h3>
                <p className="text-gray-600">{whyChooseUsContent}</p>
              </div>
              <div className="bg-gray-50 p-8 rounded-lg">
                <h3 className="text-xl font-semibold mb-4">Local Benefits</h3>
                <p className="text-gray-600">{benefitsContent}</p>
              </div>
            </div>
          </div>

          {/* Emergency Services */}
          <div className="mt-24 bg-blue-50 rounded-lg p-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                24/7 Emergency Plumbing in {city.name}
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Fast response times for all plumbing emergencies
              </p>
              <a
                href="tel:(855) 513-0871"
                className="inline-flex items-center px-8 py-4 bg-blue-600 text-white rounded-full text-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                <Phone className="w-6 h-6 mr-2" />
                Call Now: (855) 513-0871
              </a>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="mt-24 max-w-4xl mx-auto">
            <FAQSection
              faqs={faqs}
              title={`Frequently Asked Questions about Plumbing in ${city.name}`}
              description={`Get answers to common questions about plumbing services in ${city.name}, ${city.state.code}`}
            />
          </div>
        </div>
      </div>
      <SchemaMarkup type="Service" data={{ title: `Plumbing Services in ${city.name}, ${city.state.code}`, description: city.description, city: city.name, city_id: city.id }} />
    </>
  );
}

export { CityPage };